import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Fuel, Plus, Edit2, Trash2, CheckCircle, X, Package, Users,
  Inbox, Star, ChevronRight, Loader2, ExternalLink, Eye, RefreshCw, Tag, AlertTriangle, Image, FileText,
  Sparkles, ShieldCheck, GitMerge, Trophy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fügt ?token= zu geschützten API-URLs hinzu (für <img src> und direktes Öffnen)
const withToken = (url) => {
  if (!url) return null;
  const token = localStorage.getItem('token');
  if (!token) return url;
  // Relative URL → absolut machen
  const fullUrl = url.startsWith('http')
    ? url
    : `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}token=${token}`;
};

// ─────────────────────────────────────────────────────────
// Brand-Karte im Marken-Management
// ─────────────────────────────────────────────────────────
const BrandCard = ({ brand, customerCount, onEdit, onDelete }) => (
  <div
    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:shadow-md transition-shadow"
    data-testid={`brand-card-${brand.id}`}
  >
    <div
      className="w-8 h-8 rounded-full flex-shrink-0 border-2 border-white shadow-sm"
      style={{ backgroundColor: brand.color }}
    />
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-sm">{brand.name}</p>
      <p className="text-xs text-muted-foreground">{customerCount} Kunde{customerCount !== 1 ? 'n' : ''}</p>
    </div>
    <Badge variant={brand.is_active ? 'default' : 'secondary'} className="text-xs">
      {brand.is_active ? 'Aktiv' : 'Inaktiv'}
    </Badge>
    <div className="flex gap-1">
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(brand)}>
        <Edit2 className="h-3.5 w-3.5" />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => onDelete(brand)}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────
// Eingangskorb-Karte (Produktvorschlag)
// ─────────────────────────────────────────────────────────
const SuggestionCard = ({ product, onApprove, onReject, onEnrich, duplicateCount = 0, isSelected = false, onToggleSelect }) => {
  const brand = product.brand || product.submitted_by_brand || '';
  const media = product.brand_media?.[brand] || {};
  const hasImage = !!media.image_url;
  const hasPdf = !!media.pdf_url;
  const artNr = (product.brand_numbers || {})[brand];

  return (
  <div
    className={`p-4 rounded-xl border-2 transition-shadow cursor-default ${
      isSelected
        ? 'border-green-400 bg-green-50/60 dark:bg-green-950/20 shadow-md'
        : 'border-yellow-200 dark:border-yellow-800 bg-yellow-50/40 dark:bg-yellow-950/10 hover:shadow-md'
    }`}
    data-testid={`suggestion-${product.id}`}
  >
    <div className="flex items-start gap-3">
      {/* Checkbox */}
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onToggleSelect(product.id)}
        className="mt-1 h-4 w-4 rounded border-gray-300 accent-green-600 cursor-pointer flex-shrink-0"
        data-testid={`select-${product.id}`}
        onClick={e => e.stopPropagation()}
      />
      {/* Thumbnail */}
      <div className="relative w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {hasImage ? (
          <img src={withToken(media.image_url)} alt="" className="w-full h-full object-cover" />
        ) : (
          <Package className="h-5 w-5 text-gray-400" />
        )}
        {hasPdf && (
          <span className="absolute -bottom-0.5 -right-0.5 bg-blue-500 rounded-full p-0.5">
            <FileText className="h-2.5 w-2.5 text-white" />
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-sm">{product.product_name}</h3>
          <Badge variant="outline" className="text-xs border-yellow-400 text-yellow-700">
            {brand}
          </Badge>
          {duplicateCount > 1 && (
            <Badge variant="outline" className="text-xs border-red-400 text-red-600 bg-red-50">
              <AlertTriangle className="h-3 w-3 mr-1" />{duplicateCount}× vorhanden
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Von: {product.submitted_by_customer_name} • {new Date(product.created_at).toLocaleDateString('de-DE')}
        </p>
        {product.ean && <p className="text-xs font-mono text-gray-500">EAN: {product.ean}</p>}
        {artNr && <p className="text-xs font-mono text-gray-500">Art.Nr.: {artNr}</p>}
        {/* Media-Status */}
        <div className="flex items-center gap-2 mt-1">
          {hasImage ? (
            <span className="inline-flex items-center gap-0.5 text-xs text-green-600"><Image className="h-3 w-3" /> Bild</span>
          ) : (
            <span className="inline-flex items-center gap-0.5 text-xs text-gray-400"><Image className="h-3 w-3" /> kein Bild</span>
          )}
          {hasPdf ? (
            <span className="inline-flex items-center gap-0.5 text-xs text-blue-600"><FileText className="h-3 w-3" /> PDF</span>
          ) : (
            <span className="inline-flex items-center gap-0.5 text-xs text-gray-400"><FileText className="h-3 w-3" /> kein PDF</span>
          )}
        </div>
      </div>
    </div>
    <div className="flex gap-2 mt-3 pt-3 border-t border-yellow-200 dark:border-yellow-800">
      <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => onApprove(product)} data-testid={`approve-${product.id}`}>
        <CheckCircle className="h-3 w-3 mr-1" />Freigeben
      </Button>
      <Button size="sm" variant="outline" className="h-7 text-xs border-blue-400 text-blue-700" onClick={() => onEnrich(product)} data-testid={`enrich-${product.id}`}>
        <Edit2 className="h-3 w-3 mr-1" />Veredeln
      </Button>
      <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => onReject(product)} data-testid={`reject-${product.id}`}>
        <X className="h-3 w-3 mr-1" />Ablehnen
      </Button>
    </div>
  </div>
  );
};

// ─────────────────────────────────────────────────────────
// Helper: Bestes verfügbares Bild aus einem Master-Produkt
// ─────────────────────────────────────────────────────────
const getBestImage = (product) => {
  if (!product) return null;
  // 1. Brand-spezifisches Bild
  if (product.brand_media?.[product.brand]?.image_url)
    return product.brand_media[product.brand].image_url;
  // 2. Erstes beliebiges Bild aus brand_media
  const anyBrandMedia = Object.values(product.brand_media || {}).find(m => m?.image_url);
  if (anyBrandMedia?.image_url) return anyBrandMedia.image_url;
  // 3. Direktes image_url Feld (legacy)
  if (product.image_url) return product.image_url;
  return null;
};

// ─────────────────────────────────────────────────────────
// Master-Produkt-Zeile
// ─────────────────────────────────────────────────────────
const MasterProductRow = ({ product, onEdit, onDelete }) => {
  const img = getBestImage(product);
  const score = product.quality_score ?? 0;
  const scoreColors = ['bg-red-100 text-red-600', 'bg-orange-100 text-orange-600', 'bg-yellow-100 text-yellow-700', 'bg-blue-100 text-blue-600', 'bg-green-100 text-green-700'];
  return (
  <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
    <td className="py-3 px-4">
      <div className="flex items-center gap-3">
        {img ? (
          <img src={withToken(img)} className="w-8 h-8 rounded object-cover border" alt="" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
        ) : null}
        <div className={`w-8 h-8 rounded bg-gray-200 dark:bg-gray-700 items-center justify-center flex-shrink-0 ${img ? 'hidden' : 'flex'}`}>
          <Package className="h-4 w-4 text-gray-400" />
        </div>
        <div>
          <span className="font-medium text-sm">{product.product_name}</span>
          <span className={`ml-2 inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${scoreColors[score] || scoreColors[0]}`} title="Datenqualitäts-Score (Bild + PDF + EAN + Beschreibung)">
            <Trophy className="h-2.5 w-2.5" /> {score}/4
          </span>
        </div>
      </div>
    </td>
    <td className="py-3 px-4 text-xs font-mono text-gray-600 dark:text-gray-400">{product.ean || '–'}</td>
    <td className="py-3 px-4">
      <div className="flex flex-wrap gap-1">
        {Object.entries(product.brand_numbers || {}).map(([b, n]) => (
          <span key={b} className="inline-flex items-center gap-0.5 text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
            <span className="font-semibold">{b}:</span> {n}
          </span>
        ))}
      </div>
    </td>
    <td className="py-3 px-4 text-xs text-gray-600">{product.submitted_by_customer_name || 'Superadmin'}</td>
    <td className="py-3 px-4">
      <div className="flex gap-1 items-center">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(product)}>
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
        {product.has_pending_suggestions && (
          <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-medium" title="Vorschläge ausstehend">
            <Star className="h-3 w-3" /> Vorschläge
          </span>
        )}
        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => onDelete(product)}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </td>
  </tr>
  );
};

// ─────────────────────────────────────────────────────────
// Haupt-Seite
// ─────────────────────────────────────────────────────────
export const TankstellenHub = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [brands, setBrands] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [masterProducts, setMasterProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [assigningCustomer, setAssigningCustomer] = useState(null);
  const [assignBrandValue, setAssignBrandValue] = useState('');
  const [savingBrand, setSavingBrand] = useState(false);
  const [inboxBrandFilter, setInboxBrandFilter] = useState('');  // P1: Brand-Filter für Eingangskorb
  const [brandNumSelect, setBrandNumSelect] = useState('');
  const [brandNumInput, setBrandNumInput] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set()); // Bulk-Select
  const [bulkApproving, setBulkApproving] = useState(false);

  // ── Bereinigung / Cleanup ──────────────────────────────────────────────────
  const [cleanupGroups, setCleanupGroups] = useState(null);   // null=nicht geprüft, []=fertig
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupExecuting, setCleanupExecuting] = useState(false);
  // winnerMap: { group_key: winner_id }
  const [winnerMap, setWinnerMap] = useState({});

  // ── Phase 2: Medien-Vorschläge ─────────────────────────────────────────────
  const [pendingMediaProducts, setPendingMediaProducts] = useState([]);

  // Dialogs
  const [editBrand, setEditBrand] = useState(null);
  const [showBrandDialog, setShowBrandDialog] = useState(false);
  const [deleteBrand, setDeleteBrand] = useState(null);
  const [enrichProduct, setEnrichProduct] = useState(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [rejectProduct, setRejectProduct] = useState(null);

  // Forms
  const [brandForm, setBrandForm] = useState({ name: '', color: '#6B7280', is_active: true });
  const [productForm, setProductForm] = useState({});
  const [artNrDuplicateWarning, setArtNrDuplicateWarning] = useState(null); // { brand, article_number, existing_name }

  useEffect(() => { loadAll(); }, []);

  const handleRetroImport = async () => {
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/superadmin/import-retroactive?industry=tankstelle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { imported, skipped, errors, customers_processed } = res.data;
      if (imported > 0) {
        toast.success(`${imported} Produkte importiert! (${skipped} bereits vorhanden, ${customers_processed} Kunden geprüft)`);
      } else {
        toast.info(`Keine neuen Produkte gefunden. ${skipped} bereits im Eingangskorb, ${customers_processed} Kunden geprüft.`);
      }
      if (errors > 0) toast.error(`${errors} Fehler beim Import`);
      loadAll(); // Eingangskorb neu laden
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [brandsRes, suggestionsRes, masterRes, customersRes, pendingMediaRes] = await Promise.all([
        axios.get(`${API}/superadmin/brands?industry=tankstelle`),
        axios.get(`${API}/superadmin/master-products/suggestions?industry=tankstelle`),
        axios.get(`${API}/superadmin/master-products?industry=tankstelle&status=approved`),
        axios.get(`${API}/superadmin/customers`),
        axios.get(`${API}/superadmin/master-products/pending-media?industry=tankstelle`).catch(() => ({ data: [] })),
      ]);
      setBrands(brandsRes.data || []);
      setSuggestions(suggestionsRes.data || []);
      setMasterProducts(masterRes.data || []);
      setCustomers(customersRes.data || []);
      setPendingMediaProducts(pendingMediaRes.data || []);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignBrand = async () => {
    if (!assigningCustomer || !assignBrandValue) return;
    setSavingBrand(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        `${API}/superadmin/customers/${assigningCustomer.id}/assign-brand`,
        { brand: assignBrandValue, industry: 'tankstelle' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`"${assignBrandValue}" wurde ${assigningCustomer.company_name} zugewiesen. ${res.data.users_updated} User aktualisiert.`);
      setAssigningCustomer(null);
      setAssignBrandValue('');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Zuweisen');
    } finally {
      setSavingBrand(false);
    }
  };


  const tankstelleCustomers = customers.filter(c => c.industry === 'tankstelle');
  const activeCustomers = tankstelleCustomers.filter(c => c.status === 'active');
  const brandCounts = tankstelleCustomers.reduce((acc, c) => {
    if (c.brand) acc[c.brand] = (acc[c.brand] || 0) + 1;
    return acc;
  }, {});
  // Count pending brand-number suggestions per brand
  const pendingSuggestionsPerBrand = masterProducts.reduce((acc, p) => {
    Object.entries(p.brand_number_suggestions || {}).forEach(([brand, nums]) => {
      if ((nums || []).length > 0) acc[brand] = (acc[brand] || 0) + nums.length;
    });
    return acc;
  }, {});

  const goToInboxWithFilter = (brand) => {
    setInboxBrandFilter(brand);
    setActiveTab('inbox');
  };

  // Brand CRUD
  const openNewBrand = () => {
    setBrandForm({ name: '', color: '#E30613', is_active: true });
    setEditBrand(null);
    setShowBrandDialog(true);
  };
  const openEditBrand = (brand) => {
    setBrandForm({ name: brand.name, color: brand.color, is_active: brand.is_active });
    setEditBrand(brand);
    setShowBrandDialog(true);
  };
  const saveBrand = async () => {
    try {
      if (editBrand) {
        await axios.patch(`${API}/superadmin/brands/${editBrand.id}`, brandForm);
        toast.success('Marke aktualisiert');
      } else {
        await axios.post(`${API}/superadmin/brands`, { ...brandForm, industry: 'tankstelle' });
        toast.success('Marke angelegt');
      }
      setShowBrandDialog(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };
  const confirmDeleteBrand = async () => {
    try {
      await axios.delete(`${API}/superadmin/brands/${deleteBrand.id}`);
      toast.success('Marke gelöscht');
      setDeleteBrand(null);
      loadAll();
    } catch { toast.error('Fehler beim Löschen'); }
  };

  // Suggestion actions (bestehend: Produkt-Vorschläge)
  const approveSuggestion = async (p) => {
    try {
      await axios.patch(`${API}/superadmin/master-products/${p.id}/approve`);
      toast.success(`"${p.product_name}" freigegeben`);
      loadAll();
    } catch { toast.error('Fehler'); }
  };
  const rejectSuggestion = async () => {
    try {
      await axios.patch(`${API}/superadmin/master-products/${rejectProduct.id}/reject`, { reason: '' });
      toast.success('Abgelehnt');
      setRejectProduct(null);
      loadAll();
    } catch { toast.error('Fehler'); }
  };

  // Learning System: Brand-Number Vorschläge genehmigen / ablehnen
  const approveBrandNumberSuggestion = async (productId, brand, articleNumber) => {
    try {
      await axios.post(`${API}/superadmin/master-products/${productId}/approve-suggestion`, { brand, article_number: articleNumber });
      toast.success(`Art.Nr. "${articleNumber}" für ${brand} übernommen`);
      loadAll();
    } catch { toast.error('Fehler beim Übernehmen'); }
  };
  const rejectBrandNumberSuggestion = async (productId, brand) => {
    try {
      await axios.delete(`${API}/superadmin/master-products/${productId}/reject-suggestion?brand=${encodeURIComponent(brand)}`);
      toast.success(`Vorschlag für ${brand} abgelehnt`);
      loadAll();
    } catch { toast.error('Fehler beim Ablehnen'); }
  };
  const openEnrich = (p) => {
    setProductForm({
      product_name: p.product_name,
      ean: p.ean || '',
      brand: p.brand,
      brand_numbers: p.brand_numbers || {},
      brand_media: p.brand_media || {},   // ← Bilder/PDFs mitladen
      description: p.description || '',
    });
    setEnrichProduct(p);
    setShowProductDialog(true);
    setArtNrDuplicateWarning(null);
  };
  const saveEnrichedProduct = async () => {
    try {
      await axios.patch(`${API}/superadmin/master-products/${enrichProduct.id}`, productForm);
      toast.success('Produkt veredelt');
      setShowProductDialog(false);
      loadAll();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const saveAndApproveProduct = async () => {
    try {
      await axios.patch(`${API}/superadmin/master-products/${enrichProduct.id}`, productForm);
      await approveSuggestion(enrichProduct);
      setShowProductDialog(false);
      loadAll();
    } catch { toast.error('Fehler beim Speichern & Freigeben'); }
  };
  const deleteProduct = async (p) => {
    if (!window.confirm(`"${p.product_name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/superadmin/master-products/${p.id}`);
      toast.success('Gelöscht');
      loadAll();
    } catch { toast.error('Fehler'); }
  };

  // Duplikat-Zähler: Wie oft kommt Art.Nr. für diese Marke im Eingangskorb vor?
  const getDuplicateCount = (product) => {
    const brand = product.brand || product.submitted_by_brand || '';
    const artNr = (product.brand_numbers || {})[brand] || '';
    if (!artNr) return 1;
    return suggestions.filter(s => {
      const sb = s.brand || s.submitted_by_brand || '';
      const sa = (s.brand_numbers || {})[sb] || '';
      return sb === brand && sa === artNr;
    }).length;
  };

  const deduplicateInbox = async () => {
    if (!window.confirm('Alle Duplikate im Eingangskorb zusammenführen? Dies kann nicht rückgängig gemacht werden.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/superadmin/master-products/deduplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Bereinigen');
    }
  };

  // ── Phase 2: Medien-Vorschläge annehmen / ablehnen ───────────────────────
  const handleApproveMedia = async (productId, suggestionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/superadmin/master-products/${productId}/approve-media/${suggestionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bild in den Master-Katalog übernommen');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Annehmen');
    }
  };

  const handleRejectMedia = async (productId, suggestionId) => {
    if (!window.confirm('Medien-Vorschlag ablehnen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/superadmin/master-products/${productId}/pending-media/${suggestionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info('Vorschlag abgelehnt');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Ablehnen');
    }
  };
  const runCleanupPreview = async () => {
    setCleanupLoading(true);
    setCleanupGroups(null);
    setWinnerMap({});
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/superadmin/master-products/deduplicate-preview?industry=tankstelle`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const groups = res.data.groups || [];
      setCleanupGroups(groups);
      // Empfohlene Gewinner vorausfüllen
      const defaults = {};
      groups.forEach(g => { defaults[g.group_key] = g.recommended_winner_id; });
      setWinnerMap(defaults);
      if (groups.length === 0) toast.info('Keine Duplikate gefunden – der Master-Katalog ist sauber.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Prüfen');
    } finally {
      setCleanupLoading(false);
    }
  };

  // ── Cleanup: Merge ausführen ────────────────────────────────────────────────
  const executeCleanup = async () => {
    if (!cleanupGroups || cleanupGroups.length === 0) return;
    if (!window.confirm(`${cleanupGroups.length} Duplikat-Gruppe(n) zusammenführen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    setCleanupExecuting(true);
    try {
      const token = localStorage.getItem('token');
      const merges = cleanupGroups.map(g => {
        const winner_id = winnerMap[g.group_key] || g.recommended_winner_id;
        const loser_ids = g.products.filter(p => p.id !== winner_id).map(p => p.id);
        return { winner_id, loser_ids };
      });
      const res = await axios.post(
        `${API}/superadmin/master-products/deduplicate-execute`,
        { merges },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setCleanupGroups(null);
      setWinnerMap({});
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Zusammenführen');
    } finally {
      setCleanupExecuting(false);
    }
  };

  // ── Bulk-Select Helpers ────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const visibleSuggestions = inboxBrandFilter
    ? suggestions.filter(s => s.brand === inboxBrandFilter)
    : suggestions;

  const allVisibleSelected = visibleSuggestions.length > 0 &&
    visibleSuggestions.every(s => selectedIds.has(s.id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        visibleSuggestions.forEach(s => next.delete(s.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        visibleSuggestions.forEach(s => next.add(s.id));
        return next;
      });
    }
  };

  const bulkApprove = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} Produkte freigeben?`)) return;
    setBulkApproving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/superadmin/master-products/bulk-approve`,
        { product_ids: Array.from(selectedIds) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setSelectedIds(new Set());
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Freigeben');
    } finally {
      setBulkApproving(false);
    }
  };

  const totalPendingMedia = pendingMediaProducts.reduce((acc, p) => acc + (p.pending_media?.length || 0), 0);

  const tabs = [
    { id: 'overview', label: 'Übersicht', icon: Fuel },
    { id: 'brands', label: `Marken (${brands.length})`, icon: Star },
    { id: 'inbox', label: `Eingangskorb ${suggestions.length > 0 ? `(${suggestions.length})` : ''}`, icon: Inbox, badge: suggestions.length },
    { id: 'catalog', label: `Master-Katalog (${masterProducts.length})${totalPendingMedia > 0 ? ` · ${totalPendingMedia} Vorschläge` : ''}`, icon: Package, badge: totalPendingMedia },
    { id: 'cleanup', label: 'Bereinigung', icon: Sparkles },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
            <Fuel className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Tankstellen-Hub</h1>
            <p className="text-sm text-muted-foreground">Marken-Management · Eingangskorb · Master-Katalog</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-fit flex-wrap">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all relative ${
                activeTab === t.id
                  ? 'bg-white dark:bg-gray-900 shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              {t.badge > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ──────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Tankstellen-Kunden', value: tankstelleCustomers.length, icon: Users, color: 'blue' },
                { label: 'Aktive Kunden', value: activeCustomers.length, icon: CheckCircle, color: 'green' },
                { label: 'Marken registriert', value: Object.keys(brandCounts).length, icon: Star, color: 'yellow' },
                { label: 'Master-Produkte', value: masterProducts.length, icon: Package, color: 'purple' },
              ].map(s => (
                <Card key={s.label}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-2xl font-bold">{s.value}</p>
                        <p className="text-xs text-muted-foreground">{s.label}</p>
                      </div>
                      <s.icon className={`h-8 w-8 text-${s.color}-500 opacity-70`} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Marken-Übersicht */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-500" />
                  Marken-Verteilung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {/* Dynamic: show all brands that have customers, even if not in brands collection */}
                  {Object.keys({...brandCounts, ...Object.fromEntries(brands.filter(b=>b.is_active).map(b=>[b.name,brandCounts[b.name]||0]))}).map(brandName => {
                    const count = brandCounts[brandName] || 0;
                    const color = brands.find(b => b.name === brandName)?.color || '#6B7280';
                    const pending = pendingSuggestionsPerBrand[brandName] || 0;
                    return (
                      <div key={brandName} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border">
                        <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-sm font-medium">{brandName}</span>
                        <Badge variant="secondary" className="text-xs">{count} Kunden</Badge>
                        {pending > 0 && (
                          <button
                            onClick={() => goToInboxWithFilter(brandName)}
                            className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-medium hover:bg-amber-200 transition-colors"
                            title={`${pending} offene Vorschläge für ${brandName} anzeigen`}
                          >
                            <Star className="h-3 w-3" /> {pending} offen
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {suggestions.length > 0 && (
                  <div className="mt-4 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Inbox className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                        {suggestions.length} {suggestions.length === 1 ? 'Vorschlag' : 'Vorschläge'} im Eingangskorb
                      </span>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setActiveTab('inbox')}>
                      Anzeigen <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Kunden-Tabelle */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4" />Tankstellen-Kunden ({tankstelleCustomers.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 dark:bg-gray-800">
                      <th className="text-left p-3 font-medium">Unternehmen</th>
                      <th className="text-left p-3 font-medium">Marke</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">Kd.-Nr.</th>
                      <th className="text-left p-3 font-medium">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tankstelleCustomers.map(c => (
                      <tr key={c.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="p-3 font-medium">{c.company_name}</td>

                        {/* Marken-Zelle */}
                        <td className="p-3">
                          {c.brand ? (
                            <div className="flex items-center gap-1.5 group">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: brands.find(b => b.name === c.brand)?.color || '#999' }} />
                              <span className="text-xs font-medium">{c.brand}</span>
                              <button
                                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 transition-opacity ml-1"
                                title="Marke ändern"
                                onClick={() => { setAssigningCustomer(c); setAssignBrandValue(c.brand); }}
                                data-testid={`change-brand-${c.id}`}
                              >
                                <Edit2 className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Keine Marke</span>
                          )}
                        </td>

                        <td className="p-3">
                          <Badge variant={c.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                            {c.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-xs text-gray-500">{c.customer_number || '–'}</td>

                        {/* Aktion */}
                        <td className="p-3">
                          <Button
                            size="sm"
                            variant={c.brand ? 'ghost' : 'outline'}
                            className="h-7 text-xs"
                            onClick={() => { setAssigningCustomer(c); setAssignBrandValue(c.brand || ''); }}
                            data-testid={`assign-brand-btn-${c.id}`}
                          >
                            <Tag className="h-3 w-3 mr-1" />
                            {c.brand ? 'Ändern' : 'Marke zuweisen'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── MARKEN-ZUWEISUNG DIALOG ─────────────────────── */}
        <Dialog open={!!assigningCustomer} onOpenChange={(o) => { if (!o) { setAssigningCustomer(null); setAssignBrandValue(''); } }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-primary" />
                Marke zuweisen
              </DialogTitle>
            </DialogHeader>
            <div className="py-2 space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Kunde</p>
                <p className="font-semibold">{assigningCustomer?.company_name}</p>
              </div>
              <div>
                <Label htmlFor="brand-select" className="text-sm font-medium">Marke auswählen</Label>
                <select
                  id="brand-select"
                  className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                  value={assignBrandValue}
                  onChange={e => setAssignBrandValue(e.target.value)}
                  data-testid="brand-select-dropdown"
                >
                  <option value="">-- Marke wählen --</option>
                  {brands.filter(b => b.is_active && b.industry === 'tankstelle').map(b => (
                    <option key={b.id} value={b.name}>{b.name}</option>
                  ))}
                </select>
              </div>
              {assignBrandValue && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: brands.find(b => b.name === assignBrandValue)?.color || '#999' }} />
                  <span className="text-sm font-medium">{assignBrandValue}</span>
                  <span className="text-xs text-muted-foreground ml-auto">wird zugewiesen</span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setAssigningCustomer(null); setAssignBrandValue(''); }}>
                Abbrechen
              </Button>
              <Button
                onClick={handleAssignBrand}
                disabled={!assignBrandValue || savingBrand}
                data-testid="confirm-assign-brand-btn"
              >
                {savingBrand ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Zuweisen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── MARKEN-MANAGEMENT ────────────────────────── */}
        {activeTab === 'brands' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Marken verwalten</h2>
              <Button onClick={openNewBrand} size="sm" data-testid="add-brand-btn">
                <Plus className="h-4 w-4 mr-1" />Neue Marke
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {brands.map(b => (
                <BrandCard
                  key={b.id}
                  brand={b}
                  customerCount={brandCounts[b.name] || 0}
                  onEdit={openEditBrand}
                  onDelete={setDeleteBrand}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── EINGANGSKORB ──────────────────────────────── */}
        {activeTab === 'inbox' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-lg font-semibold">Eingangskorb – Produktvorschläge</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Neue Produkte von Pächtern erscheinen hier zur Prüfung.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Brand-Filter */}
                <select
                  value={inboxBrandFilter}
                  onChange={e => { setInboxBrandFilter(e.target.value); setSelectedIds(new Set()); }}
                  className="h-8 text-xs border rounded-md px-2 bg-background"
                  data-testid="inbox-brand-filter"
                >
                  <option value="">Alle Marken ({suggestions.length})</option>
                  {[...new Set(suggestions.map(s => s.brand).filter(Boolean))].map(brand => (
                    <option key={brand} value={brand}>
                      {brand} ({suggestions.filter(s => s.brand === brand).length})
                    </option>
                  ))}
                </select>
                {inboxBrandFilter && (
                  <button onClick={() => setInboxBrandFilter('')} className="text-xs text-muted-foreground hover:text-foreground">
                    Filter ×
                  </button>
                )}
                <Badge variant="outline">
                  {inboxBrandFilter
                    ? `${suggestions.filter(s => s.brand === inboxBrandFilter).length} von ${suggestions.length}`
                    : `${suggestions.length} ausstehend`}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRetroImport}
                  disabled={importing}
                  data-testid="retro-import-btn"
                  title="Alle bestehenden Backshop-Produkte von JET-Kunden in den Eingangskorb importieren"
                >
                  {importing
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  }
                  {importing ? 'Importiere…' : 'Bestehende importieren'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={deduplicateInbox}
                  data-testid="deduplicate-btn"
                  title="Alle Duplikate (gleiche Artikelnummer) zusammenführen"
                >
                  <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
                  Duplikate bereinigen
                </Button>
              </div>
            </div>

            {/* ── Bulk-Aktionsleiste (erscheint bei Auswahl) ── */}
            {suggestions.length > 0 && (
              <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-all ${
                selectedIds.size > 0
                  ? 'bg-green-50 dark:bg-green-950/20 border-green-300 dark:border-green-700'
                  : 'bg-muted/30 border-border'
              }`} data-testid="bulk-toolbar">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 accent-green-600 cursor-pointer"
                    data-testid="select-all-checkbox"
                  />
                  <span className="text-xs font-medium">
                    {allVisibleSelected ? 'Alle abwählen' : `Alle auswählen (${visibleSuggestions.length})`}
                  </span>
                </label>
                {selectedIds.size > 0 && (
                  <>
                    <span className="text-xs text-muted-foreground">|</span>
                    <span className="text-xs font-semibold text-green-700" data-testid="selected-count">
                      {selectedIds.size} ausgewählt
                    </span>
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white ml-auto"
                      onClick={bulkApprove}
                      disabled={bulkApproving}
                      data-testid="bulk-approve-btn"
                    >
                      {bulkApproving
                        ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                        : <CheckCircle className="h-3 w-3 mr-1.5" />
                      }
                      {bulkApproving ? 'Freigabe…' : `${selectedIds.size} Produkte freigeben`}
                    </Button>
                  </>
                )}
              </div>
            )}
            {suggestions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Inbox className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">Eingangskorb ist leer</p>
                  <p className="text-xs mt-1 mb-4">Wenn Pächter neue Produkte anlegen, erscheinen sie hier.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetroImport}
                    disabled={importing}
                    data-testid="retro-import-empty-btn"
                  >
                    {importing
                      ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    }
                    Bestehende Produkte importieren
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {visibleSuggestions.map(p => (
                  <SuggestionCard
                    key={p.id}
                    product={p}
                    onApprove={approveSuggestion}
                    onReject={setRejectProduct}
                    onEnrich={openEnrich}
                    duplicateCount={getDuplicateCount(p)}
                    isSelected={selectedIds.has(p.id)}
                    onToggleSelect={toggleSelect}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── MASTER-KATALOG ────────────────────────────── */}
        {activeTab === 'catalog' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Master-Katalog</h2>
              <Button size="sm" onClick={() => { setEnrichProduct(null); setProductForm({ product_name:'', ean:'', brand:'JET', brand_numbers:{}, description:'' }); setShowProductDialog(true); }} data-testid="add-product-btn">
                <Plus className="h-4 w-4 mr-1" />Neues Produkt
              </Button>
            </div>

            {/* ── Medien-Vorschläge (Phase 2 Scenario B) ────────── */}
            {pendingMediaProducts.length > 0 && (
              <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-amber-700 dark:text-amber-400">
                    <Image className="h-4 w-4" />
                    Medien-Vorschläge zur Genehmigung ({totalPendingMedia})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {pendingMediaProducts.map(prod => (
                    <div key={prod.id} className="space-y-2">
                      {(prod.pending_media || []).map(suggestion => (
                        <div key={suggestion.id} className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-800">
                          {/* Neues Bild (Vorschlag) */}
                          <div className="flex-shrink-0 text-center">
                            <p className="text-[10px] text-muted-foreground mb-1">Vorschlag</p>
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${suggestion.image_url}`}
                              alt=""
                              className="w-14 h-14 object-cover rounded-lg border border-amber-300"
                              onError={e => { e.target.src = ''; e.target.style.display='none'; }}
                            />
                          </div>
                          {/* Pfeil */}
                          <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          {/* Produktinfo */}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{prod.product_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Von: {suggestion.submitted_by_customer_name || '–'} · Marke: {suggestion.brand}
                            </p>
                            {prod.current_image_url && (
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Master hat bereits ein Bild</p>
                            )}
                            {!prod.current_image_url && (
                              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">Master hat noch kein Bild – kann übernommen werden</p>
                            )}
                          </div>
                          {/* Aktionen */}
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
                              onClick={() => handleApproveMedia(prod.id, suggestion.id)}
                              data-testid={`approve-media-btn-${suggestion.id}`}
                            >
                              <CheckCircle className="h-3.5 w-3.5 mr-1" />
                              Übernehmen
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50 h-8 text-xs"
                              onClick={() => handleRejectMedia(prod.id, suggestion.id)}
                              data-testid={`reject-media-btn-${suggestion.id}`}
                            >
                              <X className="h-3.5 w-3.5 mr-1" />
                              Ablehnen
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {masterProducts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Noch keine Master-Produkte. Vorschläge im Eingangskorb freigeben oder manuell anlegen.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800 border-b">
                        <th className="text-left p-3 font-medium">Produkt</th>
                        <th className="text-left p-3 font-medium">EAN</th>
                        <th className="text-left p-3 font-medium">Artikelnummern</th>
                        <th className="text-left p-3 font-medium">Einreicher</th>
                        <th className="text-left p-3 font-medium">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {masterProducts.map(p => (
                        <MasterProductRow key={p.id} product={p} onEdit={openEnrich} onDelete={deleteProduct} />
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ── BEREINIGUNG ────────────────────────────────── */}
        {activeTab === 'cleanup' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Duplikat-Bereinigung</h2>
                <p className="text-sm text-muted-foreground">Findet Produkte mit gleicher Marke + Artikelnummer im Master-Katalog</p>
              </div>
              <Button onClick={runCleanupPreview} disabled={cleanupLoading} data-testid="run-cleanup-preview-btn">
                {cleanupLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Duplikate prüfen
              </Button>
            </div>

            {cleanupGroups === null && !cleanupLoading && (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <GitMerge className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">Noch keine Prüfung durchgeführt</p>
                  <p className="text-sm mt-1">Klicke auf "Duplikate prüfen" um den Master-Katalog zu analysieren</p>
                </CardContent>
              </Card>
            )}

            {cleanupGroups !== null && cleanupGroups.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <ShieldCheck className="h-10 w-10 mx-auto mb-3 text-green-500" />
                  <p className="font-semibold text-green-700 dark:text-green-400">Master-Katalog ist sauber!</p>
                  <p className="text-sm text-muted-foreground mt-1">Keine Duplikate gefunden.</p>
                </CardContent>
              </Card>
            )}

            {cleanupGroups && cleanupGroups.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between px-1">
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                    {cleanupGroups.length} Duplikat-Gruppe{cleanupGroups.length !== 1 ? 'n' : ''} gefunden
                  </p>
                  <Button onClick={executeCleanup} disabled={cleanupExecuting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="execute-cleanup-btn">
                    {cleanupExecuting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GitMerge className="h-4 w-4 mr-2" />}
                    Merge ausführen
                  </Button>
                </div>

                {cleanupGroups.map(group => (
                  <Card key={group.group_key} className="border-amber-200 dark:border-amber-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <span className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 px-2 py-0.5 rounded font-mono text-xs">
                          {group.brand}: {group.article_number}
                        </span>
                        <span className="text-muted-foreground font-normal">{group.products.length} Duplikate</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className={`grid gap-3 grid-cols-${Math.min(group.products.length, 3)}`} style={{ gridTemplateColumns: `repeat(${Math.min(group.products.length, 3)}, minmax(0, 1fr))` }}>
                        {group.products.map(p => {
                          const isWinner = (winnerMap[group.group_key] || group.recommended_winner_id) === p.id;
                          const img = getBestImage(p);
                          const scoreColors = ['border-red-300 bg-red-50 dark:bg-red-950/20', 'border-orange-300 bg-orange-50 dark:bg-orange-950/20', 'border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20', 'border-blue-300 bg-blue-50 dark:bg-blue-950/20', 'border-green-300 bg-green-50 dark:bg-green-950/20'];
                          return (
                            <div
                              key={p.id}
                              onClick={() => setWinnerMap(prev => ({ ...prev, [group.group_key]: p.id }))}
                              className={`relative p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                                isWinner ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-md' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 opacity-70'
                              }`}
                              data-testid={`cleanup-product-${p.id}`}
                            >
                              {isWinner && (
                                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 z-10">
                                  <Trophy className="h-2.5 w-2.5" /> Gewinner
                                </span>
                              )}
                              {img
                                ? <img src={withToken(img)} className="w-full h-20 object-cover rounded-lg mb-2 border" alt="" />
                                : <div className="w-full h-20 bg-gray-100 dark:bg-gray-800 rounded-lg mb-2 flex items-center justify-center"><Package className="h-8 w-8 text-gray-300" /></div>
                              }
                              <p className="font-semibold text-xs mb-1 leading-tight">{p.product_name}</p>
                              <p className="text-[10px] text-muted-foreground mb-2">Von: {p.submitted_by_customer_name || 'Superadmin'}</p>
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${scoreColors[p.quality_score ?? 0]}`}>★ {p.quality_score ?? 0}/4</span>
                                {img && <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded-full">Bild</span>}
                                {((p.brand_media || {})[group.brand] || {}).pdf_url && <span className="text-[10px] bg-purple-50 text-purple-600 border border-purple-200 px-1.5 py-0.5 rounded-full">PDF</span>}
                                {p.ean && <span className="text-[10px] bg-gray-50 text-gray-600 border border-gray-200 px-1.5 py-0.5 rounded-full">EAN</span>}
                              </div>
                              {!isWinner && <p className="text-[10px] text-center text-muted-foreground mt-2">Klicken zum Auswählen</p>}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <Dialog open={showBrandDialog} onOpenChange={setShowBrandDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editBrand ? 'Marke bearbeiten' : 'Neue Marke anlegen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name</Label>
              <Input value={brandForm.name} onChange={e => setBrandForm(p => ({ ...p, name: e.target.value }))} placeholder="z.B. JET" className="mt-1" />
            </div>
            <div>
              <Label>Farbe</Label>
              <div className="flex items-center gap-3 mt-1">
                <input type="color" value={brandForm.color} onChange={e => setBrandForm(p => ({ ...p, color: e.target.value }))} className="h-10 w-14 rounded cursor-pointer border" />
                <Input value={brandForm.color} onChange={e => setBrandForm(p => ({ ...p, color: e.target.value }))} className="font-mono w-32" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBrandDialog(false)}>Abbrechen</Button>
            <Button onClick={saveBrand} data-testid="save-brand-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Brand Dialog ───────────────────────────── */}
      <AlertDialog open={!!deleteBrand} onOpenChange={() => setDeleteBrand(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marke "{deleteBrand?.name}" löschen?</AlertDialogTitle>
            <AlertDialogDescription>Diese Aktion kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteBrand} className="bg-red-600 hover:bg-red-700">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Produkt veredeln / anlegen Dialog ────────────── */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-lg w-[95vw] flex flex-col overflow-hidden
          sm:top-[50%] sm:translate-y-[-50%] sm:max-h-[90vh]
          top-[5px] translate-y-0 max-h-[calc(100svh-10px)]">
          <DialogHeader className="flex-shrink-0 pb-2 border-b">
            <DialogTitle className="text-base leading-snug pr-6">{enrichProduct ? `Veredeln: ${enrichProduct.product_name}` : 'Neues Master-Produkt'}</DialogTitle>
          </DialogHeader>
          <div
            className="space-y-4 py-3 overflow-y-auto flex-1 px-1"
            style={{WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain'}}
          >
            <div>
              <Label>Produktname</Label>
              <Input value={productForm.product_name || ''} onChange={e => setProductForm(p => ({ ...p, product_name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>EAN</Label>
              <Input value={productForm.ean || ''} onChange={e => setProductForm(p => ({ ...p, ean: e.target.value }))} className="mt-1" placeholder="z.B. 4001234567890" />
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Input value={productForm.description || ''} onChange={e => setProductForm(p => ({ ...p, description: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="flex items-center gap-1 mb-2">Artikelnummern pro Gesellschafter</Label>

              {/* Vorhandene Art.Nr. als Chips */}
              {Object.keys(productForm.brand_numbers || {}).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {Object.entries(productForm.brand_numbers || {}).map(([b, n]) => n && (
                    <span key={b} className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 border border-green-200 px-2 py-1 rounded-full">
                      <CheckCircle className="h-3 w-3" />
                      <span className="font-semibold">{b}:</span> {n}
                      <button
                        type="button"
                        className="ml-0.5 hover:text-red-500 transition-colors"
                        onClick={() => setProductForm(p => {
                          const updated = { ...p.brand_numbers };
                          delete updated[b];
                          return { ...p, brand_numbers: updated };
                        })}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Neue Art.Nr. hinzufügen – nur anzeigen wenn noch Marken ohne Art.Nr. vorhanden */}
              {brands.filter(b => b.is_active && !(productForm.brand_numbers || {})[b.name]).length > 0 && (
                <div className="flex gap-2">
                  <select
                    className="flex-1 rounded-lg border border-input bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={brandNumSelect}
                    onChange={e => { setBrandNumSelect(e.target.value); setArtNrDuplicateWarning(null); }}
                  >
                    <option value="">Gesellschafter wählen…</option>
                    {brands.filter(b => b.is_active && !(productForm.brand_numbers || {})[b.name]).map(b => (
                      <option key={b.id} value={b.name}>{b.name}</option>
                    ))}
                  </select>
                  <Input
                    className="w-28 h-9 text-xs"
                    placeholder="Art.Nr."
                    value={brandNumInput}
                    onChange={e => { setBrandNumInput(e.target.value); setArtNrDuplicateWarning(null); }}
                    onBlur={async (e) => {
                      const val = e.target.value.trim();
                      if (!val || !brandNumSelect) return;
                      const existsInMaster = masterProducts.find(p =>
                        (p.brand_numbers || {})[brandNumSelect] === val && p.id !== enrichProduct?.id
                      );
                      const existsInInbox = suggestions.find(p =>
                        (p.brand_numbers || {})[brandNumSelect] === val && p.id !== enrichProduct?.id
                      );
                      if (existsInMaster || existsInInbox) {
                        const existing = existsInMaster || existsInInbox;
                        setArtNrDuplicateWarning({ brand: brandNumSelect, article_number: val, existing_name: existing.product_name });
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && brandNumSelect && brandNumInput.trim()) {
                        setProductForm(p => ({ ...p, brand_numbers: { ...p.brand_numbers, [brandNumSelect]: brandNumInput.trim() } }));
                        setBrandNumInput('');
                        setArtNrDuplicateWarning(null);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 px-3"
                    disabled={!brandNumSelect || !brandNumInput.trim()}
                    onClick={() => {
                      if (brandNumSelect && brandNumInput.trim()) {
                        setProductForm(p => ({ ...p, brand_numbers: { ...p.brand_numbers, [brandNumSelect]: brandNumInput.trim() } }));
                        setBrandNumInput('');
                        setArtNrDuplicateWarning(null);
                      }
                    }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Hinzufügen
                  </Button>
                </div>
              )}
              {/* Duplikat-Warnung Art.Nr. */}
              {artNrDuplicateWarning && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 mt-1.5" data-testid="artnr-duplicate-warning">
                  <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">
                    <span className="font-semibold">Art.Nr. {artNrDuplicateWarning.article_number}</span> ist für <strong>{artNrDuplicateWarning.brand}</strong> bereits vergeben: „{artNrDuplicateWarning.existing_name}"
                  </p>
                </div>
              )}
            </div>

            {/* ── Learning System: Offene Vorschläge von Partnern ── */}
            {enrichProduct && Object.keys(enrichProduct.brand_number_suggestions || {}).some(b => (enrichProduct.brand_number_suggestions[b] || []).length > 0) && (
              <div className="border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5" />
                  Vorschläge von Partnern
                </p>
                <div className="space-y-2">
                  {Object.entries(enrichProduct.brand_number_suggestions || {}).map(([brand, numbers]) =>
                    (numbers || []).map((num, idx) => (
                      <div key={`${brand}-${idx}`} className="flex items-center justify-between gap-2 bg-white dark:bg-gray-800 rounded px-3 py-1.5 border border-amber-100 dark:border-amber-800">
                        <span className="text-xs">
                          <span className="font-semibold text-amber-700 dark:text-amber-400">{brand}:</span>{' '}
                          <span className="font-mono">{num}</span>
                        </span>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-xs border-green-300 text-green-700 hover:bg-green-50"
                            onClick={() => approveBrandNumberSuggestion(enrichProduct.id, brand, num)}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" /> Übernehmen
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs text-red-500 hover:bg-red-50"
                            onClick={() => rejectBrandNumberSuggestion(enrichProduct.id, brand)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* ── Medien: Bild & PDF Status ── */}
            {enrichProduct && (
              <div className="border rounded-lg p-3 space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                  <Image className="h-3.5 w-3.5" /> Medien
                </Label>
                {(() => {
                  const brand = productForm.brand || enrichProduct.brand || '';
                  const media = (productForm.brand_media || {})[brand] || {};
                  return (
                    <div className="space-y-2">
                      {/* Bild */}
                      <div className="flex items-center gap-2 p-2 rounded bg-muted/40">
                        {media.image_url ? (
                          <img src={withToken(media.image_url)} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <Image className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          {media.image_url ? (
                            <p className="text-xs font-medium text-green-700 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">Bild vorhanden</span>
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500">Kein Bild vorhanden</p>
                          )}
                        </div>
                        {media.image_url && (
                          <button
                            type="button"
                            className="text-xs text-red-500 hover:text-red-700"
                            onClick={() => setProductForm(p => {
                              const bm = { ...(p.brand_media || {}) };
                              if (bm[brand]) { bm[brand] = { ...bm[brand], image_url: null }; }
                              return { ...p, brand_media: bm };
                            })}
                          ><X className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                      {/* PDF – markenspezifisch UND globaler Upload-Button */}
                      <div className="flex items-center gap-2 p-2 rounded bg-muted/40">
                        <div className="w-10 h-10 rounded bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                          <FileText className={`h-4 w-4 ${media.pdf_url ? 'text-blue-500' : 'text-gray-300'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          {media.pdf_url ? (
                            <div>
                              <p className="text-xs font-medium text-blue-700 flex items-center gap-1">
                                <CheckCircle className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">PDF vorhanden</span>
                              </p>
                              <button
                                type="button"
                                className="text-xs text-blue-500 underline hover:text-blue-700"
                                onClick={() => {
                                  window.open(withToken(media.pdf_url), '_blank');
                                }}
                              >
                                PDF ansehen
                              </button>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">Kein PDF vorhanden</p>
                          )}
                        </div>
                        {media.pdf_url && (
                          <button
                            type="button"
                            className="text-xs text-red-500 hover:text-red-700"
                            onClick={() => setProductForm(p => {
                              const bm = { ...(p.brand_media || {}) };
                              if (bm[brand]) { bm[brand] = { ...bm[brand], pdf_url: null, pdf_path: null }; }
                              return { ...p, brand_media: bm };
                            })}
                          ><X className="h-3.5 w-3.5" /></button>
                        )}
                      </div>

                      {/* Globales PDF hochladen (für ALLE Standorte → product_assets) */}
                      {productForm?.id && (
                        <div className="mt-2 p-2 rounded-lg border border-dashed border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-900/10">
                          <p className="text-xs font-medium text-violet-700 dark:text-violet-300 mb-1.5 flex items-center gap-1">
                            <FileText className="h-3.5 w-3.5" />
                            Globales Rezept-PDF hochladen
                          </p>
                          <p className="text-xs text-violet-600/70 dark:text-violet-400/70 mb-2">
                            Einmal hochladen → für alle Standorte verfügbar (überschreibt nicht Einzelkopien)
                          </p>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="file"
                              accept="application/pdf"
                              className="hidden"
                              data-testid="global-pdf-upload-input"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const token = localStorage.getItem('token');
                                try {
                                  const res = await axios.post(
                                    `${API}/superadmin/master-products/${productForm.id}/upload-global-pdf`,
                                    await file.arrayBuffer(),
                                    {
                                      headers: {
                                        Authorization: `Bearer ${token}`,
                                        'Content-Type': 'application/pdf',
                                      }
                                    }
                                  );
                                  toast.success(res.data.message || 'Globales PDF hochgeladen');
                                  // Produkt-Liste neu laden
                                  const fresh = await axios.get(`${API}/superadmin/master-products`, {
                                    headers: { Authorization: `Bearer ${token}` },
                                    params: { brand: brand }
                                  });
                                  setMasterProducts(fresh.data || []);
                                  e.target.value = '';
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Fehler beim Upload');
                                  e.target.value = '';
                                }
                              }}
                            />
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium transition-colors cursor-pointer"
                              data-testid="global-pdf-upload-btn">
                              <FileText className="h-3.5 w-3.5" />
                              PDF auswählen & hochladen
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── Buttons sticky – immer am unteren Rand sichtbar, auch bei offener iOS Tastatur ── */}
            <div className="sticky bottom-0 bg-background pt-3 pb-safe border-t flex flex-col gap-2 -mx-1 px-1 pb-2">
              {enrichProduct && enrichProduct.status === 'pending_review' && (
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={saveAndApproveProduct}
                  data-testid="save-and-approve-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                  Speichern & Freigeben
                </Button>
              )}
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={enrichProduct ? saveEnrichedProduct : async () => {
                    try {
                      await axios.post(`${API}/superadmin/master-products`, { ...productForm, industry: 'tankstelle' });
                      toast.success('Produkt angelegt');
                      setShowProductDialog(false);
                      loadAll();
                    } catch { toast.error('Fehler'); }
                  }}
                  variant={enrichProduct?.status === 'pending_review' ? 'outline' : 'default'}
                  data-testid="save-product-btn"
                >
                  {enrichProduct ? 'Nur speichern' : 'Anlegen'}
                </Button>
                <Button variant="ghost" className="flex-1 text-muted-foreground" onClick={() => setShowProductDialog(false)}>
                  Abbrechen
                </Button>
              </div>
            </div>
          </div>
          {/* Footer entfernt – Buttons sind sticky im Scroll-Bereich */}
        </DialogContent>
      </Dialog>

      {/* ── Reject Dialog ─────────────────────────────────── */}
      <AlertDialog open={!!rejectProduct} onOpenChange={() => setRejectProduct(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>"{rejectProduct?.product_name}" ablehnen?</AlertDialogTitle>
            <AlertDialogDescription>Der Vorschlag wird aus dem Eingangskorb entfernt.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={rejectSuggestion} className="bg-red-600 hover:bg-red-700">Ablehnen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};
